import { useState, type ReactNode } from 'react';
import { useLingui } from '@lingui/react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useTransition } from '@/hooks/useWorkflows';
import { STATE_TRANSITION_MAP, getActionLabel } from '@/types/workflow';
import { ApiError } from '@/api';
import { returnFocusOnClose } from './returnFocus';

interface TransitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  action: string;
  /**
   * The server lists this action in the item's comment_required_actions: the comment is
   * required (see isCommentRequiredFor).
   */
  commentRequired?: boolean;
  currentState: string;
  onSuccess?: () => void;
  /**
   * Offer a second field with the reason the requesting parent sees in the app (#1716).
   * Used only on the reject of a parent-app change request; the comment stays internal.
   */
  parentNoteEnabled?: boolean;
  /** Replaces the action name in the title (e.g. "Approve and add contact"). */
  title?: string;
  /**
   * What the action will do, in plain words. When set it replaces the technical
   * "state will change" preview, so the reviewer confirms the outcome, not a state.
   */
  summary?: ReactNode;
  /** Replaces the "When you confirm, the system does this:" line above a summary. */
  summaryIntro?: string;
  /** Replaces the generic "Confirm" button text. */
  confirmLabel?: string;
  /**
   * Make the reason mandatory on a reject (#1766): an `employee_data` request
   * cannot be rejected without a reason the employee sees. Implies the field.
   */
  parentNoteRequired?: boolean;
  /** One line shown above the preview, e.g. that the approver filed the request while viewing as the parent (#2171). */
  notice?: string;
  /** Gets the focus back when the dialog closes: the element that opened it. */
  returnFocusTo?: HTMLElement | null;
}

export function TransitionDialog({
  open,
  onOpenChange,
  itemId,
  action,
  commentRequired = false,
  currentState,
  onSuccess,
  parentNoteEnabled = false,
  title,
  summary,
  summaryIntro,
  confirmLabel,
  parentNoteRequired = false,
  notice,
  returnFocusTo,
}: TransitionDialogProps) {
  const { i18n } = useLingui();
  const [comment, setComment] = useState('');
  const [parentNote, setParentNote] = useState('');
  const noteRequired = parentNoteRequired && action === 'reject';
  const showParentNote = (parentNoteEnabled || parentNoteRequired) && action === 'reject';
  const missingRequiredNote = noteRequired && parentNote.trim() === '';
  const missingRequiredComment = commentRequired && comment.trim() === '';
  // A genuine failure (500 / network), NOT a lost race — the two get different
  // copy on purpose (issue #993 review).
  const [submitError, setSubmitError] = useState(false);
  const transition = useTransition();

  const nextState = STATE_TRANSITION_MAP[action] ?? action;
  const actionLabel = title ?? getActionLabel(i18n, action);

  const handleConfirm = () => {
    if (missingRequiredNote || missingRequiredComment) return;
    setSubmitError(false);
    transition.mutate(
      {
        id: itemId,
        action,
        comment: commentRequired ? comment.trim() : comment || undefined,
        parent_note: showParentNote ? parentNote.trim() || undefined : undefined,
      },
      {
        onSuccess: () => {
          setComment('');
          setParentNote('');
          onOpenChange(false);
          onSuccess?.();
        },
        onError: (error: Error) => {
          // A notice assigned to `admin` is rendered on the web inbox AND the
          // mobile-management approvals tab at once, so a double-acknowledge is
          // the ordinary case, not an edge case. Both backend outcomes mean
          // "someone got there first":
          //   422 → ErrWorkflowItemCompleted   (the common sequential race)
          //   409 → ErrWorkflowOptimisticLock  (true simultaneous writes)
          // Neither is a failure the admin can act on: close, resettle the row
          // and the badge against the truth, and say so calmly.
          // useTransition already shows the calm already-actioned message and
          // invalidates the list + pending count for both codes; the dialog's
          // job is just to get out of the way.
          const status = error instanceof ApiError ? error.status : undefined;
          if (status === 409 || status === 422) {
            setComment('');
            setParentNote('');
            onOpenChange(false);
            onSuccess?.();
            return;
          }
          // Anything else is a REAL failure — a 500, a network drop. Keep the
          // dialog open so the action can be retried, and say what actually
          // happened. Reusing the already-actioned copy here would tell the
          // admin someone beat them to it when nothing of the sort occurred.
          setSubmitError(true);
        },
      },
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setComment('');
      setParentNote('');
      setSubmitError(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent onCloseAutoFocus={returnFocusOnClose(returnFocusTo)}>
        <DialogHeader>
          <DialogTitle>{actionLabel}</DialogTitle>
          <DialogDescription>
            {summary
              ? (summaryIntro ??
                i18n._({
                  id: 'workflow.transition.summaryIntro',
                  message: 'When you confirm, the system does this:',
                }))
              : i18n._({
                  id: 'workflow.transition.statePreview',
                  message: 'State will change:',
                })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {notice && (
            <p className="text-sm text-muted-foreground" data-testid="transition-dialog-notice">
              {notice}
            </p>
          )}
          {summary ? (
            <div data-testid="transition-summary">{summary}</div>
          ) : (
            /* State transition preview */
            <div className="flex items-center gap-2">
              <Badge variant="outline">{currentState}</Badge>
              <span className="text-muted-foreground">→</span>
              <Badge variant="secondary">{nextState}</Badge>
            </div>
          )}

          {/* Comment field */}
          <div className="space-y-2">
            <Label htmlFor="transition-comment">
              {commentRequired
                ? i18n._({ id: 'workflow.transition.commentRequiredLabel', message: 'Comment' })
                : i18n._({
                    id: 'workflow.transition.comment',
                    message: 'Comment (optional)',
                  })}
            </Label>
            <Textarea
              id="transition-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={10000}
              placeholder={i18n._({
                id: 'workflow.transition.commentPlaceholder',
                message: 'Add a comment...',
              })}
              rows={3}
              disabled={transition.isPending}
              aria-required={commentRequired ? 'true' : undefined}
              aria-describedby={commentRequired ? 'transition-comment-hint' : undefined}
            />
            {commentRequired && (
              <p id="transition-comment-hint" className="text-xs text-muted-foreground">
                {i18n._({
                  id: 'workflow.transition.commentRequired',
                  message: 'A comment is required for this action.',
                })}
              </p>
            )}
            <p className="text-xs text-muted-foreground text-right">
              {comment.length}/10000
            </p>
          </div>

          {/* Reason for the parent (#1716): shown to the requester in the app. */}
          {showParentNote && (
            <div className="space-y-2">
              <Label htmlFor="transition-parent-note">
                {noteRequired
                  ? i18n._({
                      id: 'workflow.transition.employeeReason',
                      message: 'Reason for the employee',
                    })
                  : i18n._({
                      id: 'workflow.transition.parentNote',
                      message: 'Reason for the parent (optional)',
                    })}
              </Label>
              <Textarea
                id="transition-parent-note"
                data-testid="transition-parent-note"
                value={parentNote}
                onChange={(e) => setParentNote(e.target.value)}
                maxLength={1000}
                rows={3}
                disabled={transition.isPending}
                aria-required={noteRequired ? 'true' : undefined}
                aria-describedby="transition-parent-note-hint"
              />
              <p id="transition-parent-note-hint" className="text-xs text-muted-foreground">
                {noteRequired
                  ? i18n._({
                      id: 'workflow.transition.employeeReasonHint',
                      message:
                        'Required. The employee sees this text in the app. The comment above stays internal.',
                    })
                  : i18n._({
                      id: 'workflow.transition.parentNoteHint',
                      message:
                        'The parent sees this text in the app. The comment above stays internal.',
                    })}
              </p>
            </div>
          )}

          {/* A real failure. A lost race never lands here — it closes the
              dialog with the calm already-actioned toast instead. */}
          {submitError && (
            <p
              className="text-sm text-destructive"
              data-testid="transition-error"
              role="alert"
            >
              {i18n._({
                id: 'workflow.transition.submitError',
                message: 'The action could not be completed. Please try again.',
              })}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={transition.isPending}
          >
            {i18n._({ id: 'workflow.transition.cancel', message: 'Cancel' })}
          </Button>
          <Button
            variant={action === 'escalate' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={transition.isPending || missingRequiredNote || missingRequiredComment}
          >
            {transition.isPending && (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            )}
            {confirmLabel ?? i18n._({ id: 'workflow.transition.confirm', message: 'Confirm' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
